
import React, { useState } from "react";
import { Layout } from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

// FAQ data structure with categories
const faqData = [
  {
    category: "General Property Tax Questions",
    icon: "🏠",
    questions: [
      {
        question: "What is property tax?",
        answer: "Property tax is a local tax charged on real estate. It funds public services like schools, roads, fire departments, and parks. It is usually paid annually and based on the assessed value of your property."
      },
      {
        question: "How is my property tax calculated?",
        answer: "Your annual property tax bill is calculated as: Assessed Value × Mill Levy = Property Tax Owed"
      },
      {
        question: "What is the difference between assessed value and actual market value?",
        answer: "The actual market value is what your home would sell for in today's market. The assessed value is a percentage of that amount, determined by your local government for tax purposes. For example, in some states (like Colorado), residential properties are assessed at only a portion (e.g., 6.95%) of their actual value."
      },
      {
        question: "Why does my neighbor pay less in taxes than I do?",
        answer: "Your neighbor may have a smaller lot, a lower assessed value, fewer improvements, or qualify for exemptions. Mill levies can also vary slightly by district or taxing authority."
      }
    ]
  },
  {
    category: "Understanding Assessments",
    icon: "📊",
    questions: [
      {
        question: "What is a property assessment?",
        answer: "A property assessment is your local government's estimate of your property's value for tax purposes. It's updated periodically (often annually or biennially)."
      },
      {
        question: "Who determines my assessment?",
        answer: "Your local county assessor's office is responsible for assessing property values."
      },
      {
        question: "What is the difference between appraisal and assessment?",
        answer: "An appraisal is usually done by a private appraiser (often for mortgage lending) and reflects current market conditions. An assessment is performed by the government to determine property taxes and may lag behind market value."
      },
      {
        question: "How often is my property reassessed?",
        answer: "This depends on your location. Some counties reassess annually, while others do it every 2 or 4 years."
      }
    ]
  },
  {
    category: "Mill Levy & Tax Rate",
    icon: "💵",
    questions: [
      {
        question: "What is a mill levy?",
        answer: "A mill levy is the tax rate applied to the assessed value of your property. One mill equals $1 of tax per $1,000 of assessed value. For example, if your assessed value is $50,000 and the mill levy is 75, your tax would be: $50,000 × 0.075 = $3,750"
      },
      {
        question: "Who sets the mill levy?",
        answer: "Mill levies are set by various local taxing authorities—like school districts, fire departments, and municipalities—and combined to form your total property tax rate."
      },
      {
        question: "Why do mill levies vary between areas?",
        answer: "Different areas fund different services at different levels. A county with more schools or higher public service budgets may have a higher combined mill levy."
      }
    ]
  },
  {
    category: "Appealing Your Property Tax Assessment",
    icon: "📅",
    questions: [
      {
        question: "Can I appeal my property assessment?",
        answer: "Yes. Most counties allow property owners to file an appeal if they believe their home is over-assessed."
      },
      {
        question: "How do I appeal my assessment?",
        answer: "You typically file a protest with your county assessor's office, often online, by mail, or in person. Each county has its own deadlines and process."
      },
      {
        question: "What is the deadline to appeal?",
        answer: "This varies by county and state, but most appeal periods are in the spring or summer. Always check with your local assessor."
      },
      {
        question: "What evidence do I need for an appeal?",
        answer: "The most common evidence includes: Comparable recent home sales (comps), a recent appraisal, and photos or documentation of issues that affect value."
      },
      {
        question: "What happens if I win my appeal?",
        answer: "Your assessed value (and therefore your tax bill) may be reduced. The change could apply to the current year and, in some cases, future years."
      }
    ]
  },
  {
    category: "Other Common Questions",
    icon: "🧾",
    questions: [
      {
        question: "Can I appeal my taxes every year?",
        answer: "Yes, in most places you can file an appeal annually during the designated window."
      },
      {
        question: "Will appealing affect my home's resale value?",
        answer: "No. Appealing your tax assessment does not change your market value or affect your title or sale price."
      },
      {
        question: "Is there a cost to file an appeal?",
        answer: "No, filing an appeal is usually free. You may choose to hire a professional, but it is not required."
      },
      {
        question: "I found incorrect data on my home—can I fix it?",
        answer: "Maybe. If the data came from public records, you must contact your county assessor's office directly. This site does not control or update official property records."
      },
      {
        question: "What if I was referred by an agent?",
        answer: "If you were invited by a real estate agent, their photo and contact info may appear throughout your report to help you reconnect easily."
      }
    ]
  }
];

const FAQ = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => 
      prev.includes(category) 
        ? prev.filter(c => c !== category) 
        : [...prev, category]
    );
  };

  // Filter questions based on search query
  const filteredFaqs = searchQuery
    ? faqData.map(category => ({
        ...category,
        questions: category.questions.filter(
          q => 
            q.question.toLowerCase().includes(searchQuery.toLowerCase()) || 
            q.answer.toLowerCase().includes(searchQuery.toLowerCase())
        )
      })).filter(category => category.questions.length > 0)
    : faqData;

  return (
    <Layout>
      <div className="container mx-auto px-4 py-12">
        <h1 className="text-3xl md:text-4xl font-bold text-center mb-8 text-tax-deepBlue">
          Frequently Asked Questions
        </h1>
        
        {/* Search Bar */}
        <div className="relative max-w-xl mx-auto mb-12">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-tax-neutralGray" />
          </div>
          <Input
            type="text"
            placeholder="Search for questions..."
            className="pl-10 py-3 rounded-lg border-tax-borderGray"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        
        {/* FAQ Categories */}
        <div className="max-w-3xl mx-auto space-y-8">
          {filteredFaqs.map((category, idx) => (
            <div key={idx} className="border rounded-lg overflow-hidden bg-white shadow-sm">
              <Collapsible 
                open={expandedCategories.includes(category.category) || searchQuery !== ""}
                onOpenChange={() => !searchQuery && toggleCategory(category.category)}
              >
                <CollapsibleTrigger className="w-full flex items-center justify-between p-4 bg-tax-lightGray hover:bg-tax-lightGray/80 transition-colors cursor-pointer">
                  <div className="flex items-center">
                    <span className="text-2xl mr-3" aria-hidden="true">{category.icon}</span>
                    <h2 className="text-xl font-semibold text-tax-deepBlue">{category.category}</h2>
                  </div>
                  <div className={`transform transition-transform ${expandedCategories.includes(category.category) || searchQuery !== "" ? 'rotate-180' : ''}`}>
                    ▼
                  </div>
                </CollapsibleTrigger>
                
                <CollapsibleContent>
                  <Accordion 
                    type="single" 
                    collapsible 
                    className="w-full"
                  >
                    {category.questions.map((faq, faqIdx) => (
                      <AccordionItem 
                        key={faqIdx} 
                        value={`${category.category}-${faqIdx}`}
                        className="border-t border-tax-borderGray"
                      >
                        <AccordionTrigger className="px-4 py-3 hover:bg-tax-softWhite text-left font-medium text-tax-deepBlue">
                          {faq.question}
                        </AccordionTrigger>
                        <AccordionContent className="px-4 py-3 text-tax-neutralGray bg-tax-softWhite">
                          <p className="whitespace-pre-line">{faq.answer}</p>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </CollapsibleContent>
              </Collapsible>
            </div>
          ))}
        </div>
        
        {/* CTA Button - Updated to link to home page with property form anchor */}
        <div className="fixed bottom-6 left-0 w-full flex justify-center z-10 pointer-events-none">
          <Button asChild className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-6 py-3 shadow-lg pointer-events-auto">
            <Link to="/#property-form">
              Check My Property Assessment →
            </Link>
          </Button>
        </div>
      </div>
    </Layout>
  );
};

export default FAQ;
